import { useCallback, useEffect, useState } from 'react';
import { ErrorNote } from '../app/ui';
import { authCall } from '../lib/authCall';
import { approveStepUp, denyDevice, liveStepUps, loadStepUps, matchLine, stepUpLabel, timeLeft } from '../lib/deviceApprove';
import type { PendingStepUp } from '../lib/deviceApprove';
import { assertPasskey } from '../lib/passkey';

const POLL_MS = 5000;

/**
 * Pending hand-off step-ups of this person (GET /auth/device/stepups), polled every 5 s while the page is visible.
 * Hidden on an older gateway (404) or a session that may not list (403). CONTRACT_O1_NUMBER_MATCH, Erwin 2026-10-29.
 */
export function usePendingStepUps(intervalMs = POLL_MS, enabled = true) {
  const [items, setItems] = useState<PendingStepUp[]>([]);
  const [supported, setSupported] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    const r = await loadStepUps(authCall);
    if (r.k === 'ok') { setItems(r.items); setError(null); setSupported(true); }
    else if (r.k === 'unsupported') { setItems([]); setSupported(false); setError(null); }
    else setError(r.message);
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const tick = () => { if (live && document.visibilityState === 'visible') void refresh(); };
    tick();
    const id = window.setInterval(tick, intervalMs);
    document.addEventListener('visibilitychange', tick);
    return () => { live = false; window.clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [refresh, intervalMs, enabled]);
  return { items, supported, error, refresh, setItems };
}

function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const id = window.setInterval(() => setNow(Date.now()), ms); return () => window.clearInterval(id); }, [ms]);
  return now;
}

/** The big 2-digit number + "Only approve if your glasses show NN". */
export function MatchNumber({ n }: { n: number | null }) {
  const line = matchLine(n);
  if (n === null || !line) return null;
  return (
    <div className="match-number-box">
      <div className="match-number" aria-label={`Match number ${n}`}>{n}</div>
      <p className="match-line"><strong>{line}.</strong></p>
    </div>
  );
}

type RowState = { busy?: boolean; error?: string; done?: 'approved' | 'denied' };

export function ApproveSignIns() {
  const { items, supported, error, refresh, setItems } = usePendingStepUps();
  const now = useNow();
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [done, setDone] = useState<string | null>(null);
  if (!supported) return null;
  const list = liveStepUps(items, now);

  const set = (code: string, st: RowState) => setRows((r) => ({ ...r, [code]: st }));
  async function approve(i: PendingStepUp) {
    set(i.userCode, { busy: true });
    const r = await approveStepUp(i, { passkey: assertPasskey, call: authCall });
    if (!r.ok) { set(i.userCode, { error: r.message }); return; }
    set(i.userCode, {});
    setItems((xs) => xs.filter((x) => x.userCode !== i.userCode));
    setDone('Approved. The glasses continue by themselves.');
    void refresh();
  }
  async function deny(i: PendingStepUp) {
    set(i.userCode, { busy: true });
    const r = await denyDevice(i.userCode, authCall);
    if (!r.ok) { set(i.userCode, { error: r.message }); return; }
    set(i.userCode, {});
    setItems((xs) => xs.filter((x) => x.userCode !== i.userCode));
    setDone('Declined. The glasses were told no.');
    void refresh();
  }

  return (
    <section className="approve-signins" aria-labelledby="approve-signins-h">
      <h2 id="approve-signins-h">Approve sign-ins</h2>
      {done ? <p className="muted small" role="status">{done}</p> : null}
      {error ? <p className="muted small">{error}</p> : null}
      {list.length === 0
        ? <p className="muted small">No sign-in is waiting for you. When your glasses ask, it shows up here.</p>
        : list.map((i) => {
          const st = rows[i.userCode] ?? {};
          return (
            <div key={i.userCode} className="card inset approve-row">
              <MatchNumber n={i.matchNumber} />
              <p>{stepUpLabel(i)}</p>
              <p className="muted small">
                {timeLeft(i.expiresAt, now) ?? ''}
                {i.vouchesHeadset ? ' · approving also lets this headset count as your passkey in Helm on the glasses (never for admin)' : ''}
              </p>
              <ErrorNote text={st.error} />
              <div className="row-actions">
                <button className="btn primary" type="button" disabled={st.busy} onClick={() => void approve(i)}>{st.busy ? 'Waiting…' : 'Approve'}</button>
                <button className="btn" type="button" disabled={st.busy} onClick={() => void deny(i)}>Deny</button>
              </div>
            </div>
          );
        })}
    </section>
  );
}
